import { Dialog, DialogContent, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { glassDialogSlotProps } from "src/theme/glassDialog";
import { useT } from "src/i18n";

// Shared frosted-glass Dialog shell used by every betonijerry modal: glass Paper
// (via glassDialogSlotProps), a top-right close (X) button, and an optional
// Black Ops One title. Extracted from the four hand-rolled copies that used to
// live in AuthModal / PersonEditDialog / AsiakasEditDialog / EntityFormDialog.
//
// `busy` (e.g. status === "submitting") blocks backdrop/ESC dismissal and
// disables the X while a save is in flight. The close button carries zIndex:1 by
// construction, so a consumer can safely pass `contentSx={{ position:"relative" }}`
// to anchor an overlay without the content box swallowing clicks on the X — the
// regression that originally bit AuthModal can no longer happen here.
//
// Props:
//   open, onClose — controlled open state (parent owns it)
//   busy          — when true, close is disabled + backdrop/ESC dismissal blocked
//   title         — optional Finnish string rendered as the uppercase header
//   maxWidth      — MUI Dialog maxWidth (default "xs")
//   contentSx     — sx merged into DialogContent (override padding / position)
//   children      — dialog body (typically a <Box component="form">)
const TITLE_SX = {
    fontFamily: "'Black Ops One', serif",
    fontSize: 18,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "text.primary",
    mb: 2,
};

export default function GlassDialog({ open, onClose, busy = false, title, maxWidth = "xs", contentSx, children }) {
    const theme = useTheme();
    const t = useT();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            maxWidth={maxWidth}
            fullWidth
            fullScreen={fullScreen}
            slotProps={glassDialogSlotProps(theme)}
        >
            <IconButton
                onClick={onClose}
                aria-label={t("common.dialog.close")}
                disabled={busy}
                sx={{ position: "absolute", right: 8, top: 8, color: "text.primary", zIndex: 1 }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent sx={{ pt: 6, pb: 4, px: { xs: 3, sm: 4 }, ...contentSx }}>
                {title && <Typography sx={TITLE_SX}>{title}</Typography>}
                {children}
            </DialogContent>
        </Dialog>
    );
}
